import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useAppStatusActions } from '@/db/useAppStatusActions';
import { appStatusLiveState, requiredUpgradeEditDocState, storeUrlsEditDocState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { DEFAULT_UPGRADE_MESSAGE } from '@rock/util';
import type { AppStatusRequiredUpgrade, Timestamp } from '@rock/types';
import { type FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import upgradeShot from '@/assets/app-status/upgrade.png';
import { AppStatusGuidanceCard, LiveSince } from './AppStatusGuidanceCard';
import { AppStatusStagingToolbar } from './AppStatusStagingToolbar';
import { StoreLinksCard } from './StoreLinksCard';

// Deliberately stricter than the runtime gate's parser (which tolerates a "v" prefix, extra
// segments, whitespace): admin input should be canonical dotted-numeric like the store version.
const VERSION_PATTERN = /^\d+(\.\d+){0,2}$/;

const FormSchema = z
    .object({
        enabled: z.boolean(),
        minVersion: z.string(),
        message: z.string(),
    })
    .refine((v) => !v.enabled || VERSION_PATTERN.test(v.minVersion.trim()), {
        message: 'Use a version like 0.21.1 (numbers and dots only)',
        path: ['minVersion'],
    });

type FormValues = z.infer<typeof FormSchema>;

function toFormValues(doc: AppStatusRequiredUpgrade | undefined): FormValues {
    return {
        enabled: !!doc?.enabled,
        minVersion: doc?.minVersion ?? '',
        message: doc?.message ?? '',
    };
}

function toDoc(values: FormValues): AppStatusRequiredUpgrade {
    return {
        object: 'app-status-required-upgrade',
        schema: 1,
        id: 'required-upgrade',
        enabled: values.enabled,
        minVersion: values.minVersion.trim() || undefined,
        message: values.message.trim() || undefined,
        updatedAt: new Date().toISOString() as Timestamp,
    };
}

export const RequiredUpgradePage: FC = () => {
    const editDoc = useAsyncAtomValue(requiredUpgradeEditDocState, undefined);
    const storeUrls = useAsyncAtomValue(storeUrlsEditDocState, undefined);
    const live = useAsyncAtomValue(appStatusLiveState('required-upgrade'), undefined);
    const { busy, saveDraft } = useAppStatusActions('required-upgrade');

    const form = useForm<FormValues, unknown, FormValues>({
        resolver: zodResolver(FormSchema),
        defaultValues: toFormValues(undefined),
    });

    useEffect(() => {
        if (editDoc && !form.formState.isDirty) {
            form.reset(toFormValues(editDoc));
        }
    }, [editDoc, form]);

    const onSubmit = async (values: FormValues) => {
        await saveDraft(toDoc(values));
        form.reset(values);
    };

    const confirmMessage = editDoc?.enabled
        ? `Publishing fully blocks every install below version ${editDoc.minVersion ?? '?'} behind an ` +
          '"Update required" screen until the user updates from the store.' +
          (storeUrls?.ios?.trim()
              ? ''
              : ' WARNING: no iOS App Store URL is set (Store Links below), so iOS users will see the blocked ' +
                'screen with NO update button.')
        : undefined;

    return (
        <div className="p-6 max-w-3xl space-y-4">
            <AppStatusStagingToolbar kind="required-upgrade" confirmMessage={confirmMessage} />
            <AppStatusGuidanceCard
                title="Required Upgrade"
                screenshot={{ src: upgradeShot, alt: 'Full-screen update-required overlay with store button' }}
            >
                <p>
                    Forces a minimum native app version: any install BELOW the minimum gets a full-screen,
                    non-dismissable "Update required" screen with a button to the app store. Use it when an old version
                    must not stay in use — e.g. a security fix or a breaking backend change.
                </p>
                <p>
                    Installs at or above the minimum are untouched. Web is never gated (no store). The version compared
                    is the store version (e.g. 0.21.1), not the OTA bundle — for JS-only fixes use Forced OTA Update
                    instead.
                </p>
            </AppStatusGuidanceCard>

            <Card>
                <CardHeader>
                    <CardTitle>Minimum Version</CardTitle>
                </CardHeader>
                <CardContent>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                            <FormField
                                control={form.control}
                                name="enabled"
                                render={({ field }) => (
                                    <FormItem className="flex items-center gap-3">
                                        <FormControl>
                                            <Switch checked={field.value} onCheckedChange={field.onChange} />
                                        </FormControl>
                                        <FormLabel>Enforce a minimum version</FormLabel>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="minVersion"
                                render={({ field }) => (
                                    <FormItem className="w-48">
                                        <FormLabel>Minimum version</FormLabel>
                                        <FormControl>
                                            <Input className="bg-white" placeholder="0.21.1" {...field} />
                                        </FormControl>
                                        <FormDescription>
                                            Users below this version are fully blocked until they update.
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="message"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Message</FormLabel>
                                        <FormControl>
                                            <Textarea
                                                className="bg-white"
                                                placeholder={DEFAULT_UPGRADE_MESSAGE}
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            Shown under "Update required". Leave blank for the default above.
                                        </FormDescription>
                                    </FormItem>
                                )}
                            />
                            <div className="flex items-center gap-4">
                                <Button type="submit" disabled={form.formState.isSubmitting || busy}>
                                    {form.formState.isSubmitting ? 'Saving…' : 'Save Draft'}
                                </Button>
                                <LiveSince live={live} />
                            </div>
                        </form>
                    </Form>
                </CardContent>
            </Card>

            <StoreLinksCard />
        </div>
    );
};
